import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bot, ChevronDown, ChevronRight, Database, Loader2, RefreshCw, Send } from 'lucide-react';
import GlobalHeader from '../components/GlobalHeader';
import MarkdownMessage from '../components/agents/MarkdownMessage';
import { useTenant } from '../contexts/TenantContext';

type EngineMode = 'claude' | 'gpt';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  model?: string;
  toolsUsed?: string[];
  raw?: unknown;
}

const SUGGESTED_QUESTIONS = [
  'Should I place a Ulta PO this week?',
  'Why were my Ulta sales down last week?',
  'Which SKUs are at risk of stockout?',
  'How is Amazon performing vs Shopify this month?',
  "What's my best performing Ulta store region?",
  "When should I expect my next Macy's reorder?",
];

function getThinkingMessage(question: string): string {
  const q = question.toLowerCase();

  if (q.includes('po') || q.includes('purchase order') || q.includes('reorder')) {
    return 'Calculating PO recommendation...';
  }

  if (q.includes('inventory') || q.includes('stockout') || q.includes('wos')) {
    return 'Analyzing inventory levels...';
  }

  if (q.includes('amazon') || q.includes('shopify') || q.includes('walmart')) {
    return 'Comparing DTC channel performance...';
  }

  if (q.includes('why') || q.includes('down') || q.includes('wow') || q.includes('sales')) {
    return 'Checking sell-through data...';
  }

  return 'Analyzing retail performance...';
}

export default function AgentHub() {
  const { selectedRetailer } = useTenant();

  const [engine, setEngine] = useState<EngineMode>('claude');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingMessage, setThinkingMessage] = useState('');
  const [suggestionOffset, setSuggestionOffset] = useState(0);
  const [expandedTools, setExpandedTools] = useState<Record<string, boolean>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initializedFromQuery = useRef(false);

  useEffect(() => {
    document.title = 'Ask PIQ';
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const visibleSuggestions = useMemo(() => {
    return Array.from({ length: 4 }, (_, index) => {
      const cursor = (suggestionOffset + index) % SUGGESTED_QUESTIONS.length;
      return SUGGESTED_QUESTIONS[cursor];
    });
  }, [suggestionOffset]);

  const sendMessage = async (questionOverride?: string) => {
    const question = (questionOverride ?? input).trim();
    if (!question || isLoading) {
      return;
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: question,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setThinkingMessage(getThinkingMessage(question));

    try {
      const endpoint = engine === 'claude' ? '/api/copilot/claude-retail' : '/api/copilot/chat';

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: question,
          conversationId: conversationId || undefined,
          retailerId: selectedRetailer?.id,
        }),
      });

      const rawPayload = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = typeof rawPayload?.error === 'string'
          ? rawPayload.error
          : `Request failed with status ${response.status}`;
        throw new Error(message);
      }

      let assistantText = 'No response generated.';
      let toolsUsed: string[] = [];
      let modelName = engine === 'claude' ? 'claude-sonnet-4-5' : 'gpt';

      if (engine === 'claude') {
        assistantText = typeof rawPayload?.response === 'string' ? rawPayload.response : assistantText;
        toolsUsed = Array.isArray(rawPayload?.toolsUsed)
          ? rawPayload.toolsUsed.filter((tool: unknown): tool is string => typeof tool === 'string')
          : [];
        modelName = typeof rawPayload?.model === 'string' ? rawPayload.model : modelName;
      } else {
        assistantText = typeof rawPayload?.data?.answer === 'string'
          ? rawPayload.data.answer
          : typeof rawPayload?.answer === 'string'
            ? rawPayload.answer
            : assistantText;

        if (typeof rawPayload?.data?.functionCalled === 'string' && rawPayload.data.functionCalled.trim()) {
          toolsUsed = [rawPayload.data.functionCalled.trim()];
        }

        if (typeof rawPayload?.data?.conversationId === 'string' && rawPayload.data.conversationId) {
          setConversationId(rawPayload.data.conversationId);
        }
      }

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: assistantText,
        timestamp: new Date(),
        model: modelName,
        toolsUsed,
        raw: rawPayload,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error: any) {
      const assistantError: ChatMessage = {
        id: `assistant-error-${Date.now()}`,
        role: 'assistant',
        content: `Error: ${error.message || 'Unable to process request.'}`,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantError]);
    } finally {
      setIsLoading(false);
      setThinkingMessage('');
    }
  };

  useEffect(() => {
    if (initializedFromQuery.current) {
      return;
    }

    initializedFromQuery.current = true;

    const params = new URLSearchParams(window.location.search);
    const question = params.get('q');
    const shouldSubmit = params.get('submit') !== 'false';

    if (question) {
      setInput(question);
      if (shouldSubmit) {
        setTimeout(() => {
          void sendMessage(question);
        }, 0);
      }
      window.history.replaceState({}, '', '/agent-hub');
    }
  }, []);

  const refreshSuggestions = () => {
    setSuggestionOffset((prev) => (prev + 1) % SUGGESTED_QUESTIONS.length);
  };

  const handleSuggestionClick = (question: string) => {
    setInput(question);
    void sendMessage(question);
  };

  const toggleToolsRow = (messageId: string) => {
    setExpandedTools((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalHeader
        title="Ask PIQ"
        subtitle="Real answers from your actual retail data"
      />

      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Ask PIQ</h1>
              <p className="text-sm text-gray-600 mt-1">Real answers from your actual retail data</p>
            </div>

            <div className="text-right">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${
                engine === 'claude'
                  ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                  : 'bg-gray-100 text-gray-700 border-gray-200'
              }`}>
                {engine === 'claude' ? 'Claude' : 'GPT'}
              </span>

              <div className="mt-2 inline-flex rounded-md border border-gray-200 overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setEngine('claude')}
                  className={`px-3 py-1.5 ${engine === 'claude' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  Claude
                </button>
                <button
                  type="button"
                  onClick={() => setEngine('gpt')}
                  className={`px-3 py-1.5 ${engine === 'gpt' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  GPT
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            {messages.length === 0 && (
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                <div className="mx-auto w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-indigo-700" />
                </div>
                <h2 className="mt-4 text-xl font-semibold text-gray-900">Ask PIQ</h2>
                <p className="text-sm text-gray-600 mt-2">Real answers from your actual retail data</p>
                <p className="text-xs text-gray-500 mt-3">Powered by Claude + your Neon DB | Updated daily via OpenClaw</p>
              </div>
            )}

            {messages.length > 0 && (
              <div className="max-h-[52vh] overflow-y-auto pr-1 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[90%] rounded-xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-50 border border-gray-200 text-gray-900'
                    }`}>
                      {message.role === 'assistant' ? (
                        <>
                          <MarkdownMessage content={message.content} />

                          {message.toolsUsed && message.toolsUsed.length > 0 && (
                            <div className="mt-3 pt-2 border-t border-gray-200">
                              <button
                                type="button"
                                onClick={() => toggleToolsRow(message.id)}
                                className="w-full text-left flex items-start justify-between gap-2 text-xs text-gray-600 hover:text-gray-800"
                              >
                                <span className="flex items-center gap-2 flex-wrap">
                                  <Database className="w-3.5 h-3.5 text-indigo-500" />
                                  <span>Used:</span>
                                  {message.toolsUsed.map((tool) => (
                                    <span
                                      key={`${message.id}-${tool}`}
                                      className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-700"
                                    >
                                      {tool}
                                    </span>
                                  ))}
                                </span>
                                {expandedTools[message.id] ? (
                                  <ChevronDown className="w-4 h-4 flex-shrink-0" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 flex-shrink-0" />
                                )}
                              </button>

                              {expandedTools[message.id] && (
                                <pre className="mt-2 text-[11px] bg-white border border-gray-200 rounded-md p-2 overflow-x-auto text-gray-700">
                                  {JSON.stringify({
                                    model: message.model,
                                    toolsUsed: message.toolsUsed,
                                    responsePayload: message.raw,
                                  }, null, 2)}
                                </pre>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                      )}
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="flex justify-start">
                    <div className="max-w-[90%] rounded-xl px-4 py-3 bg-gray-50 border border-gray-200 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>{thinkingMessage || 'Analyzing retail data...'}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Suggested questions</p>
                <button
                  type="button"
                  onClick={refreshSuggestions}
                  className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  refresh suggestions
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {visibleSuggestions.map((question) => (
                  <button
                    key={question}
                    type="button"
                    onClick={() => handleSuggestionClick(question)}
                    disabled={isLoading}
                    className="text-left px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-end gap-3">
              <div className="flex-1">
                <textarea
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void sendMessage();
                    }
                  }}
                  placeholder="Should I place a Ulta PO this week?"
                  rows={2}
                  disabled={isLoading}
                  className="w-full resize-none rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-60"
                />
              </div>

              <button
                type="button"
                onClick={() => void sendMessage()}
                disabled={!input.trim() || isLoading}
                className="h-[46px] px-4 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
